/**
 * useCardDrag - Composable for card drag and reorder functionality
 *
 * Extracted from GameBoard.vue to reduce component complexity.
 * Handles:
 * - Direction-based drag intent detection (horizontal = reorder, vertical = play)
 * - Manual card reordering with persistent order
 * - Drag state machine for reorder and play modes
 * - Selected cards index updating during moves
 *
 * Usage:
 * ```typescript
 * const cardDrag = useCardDrag()
 * // ... define dependencies ...
 * cardDrag.setup({ playerHand, selectedCards, ... })
 * ```
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { Card } from './useCards'

// ============================================================================
// Types
// ============================================================================

/** Drag intent type - detected from drag direction */
export type DragIntent = 'undecided' | 'reorder' | 'play'

/** Setup options for configuring the composable */
interface UseCardDragOptions {
  /** Player's hand cards (sorted for display) */
  playerHand: ComputedRef<Card[]>
  /** Original unsorted hand from server (for index mapping) */
  originalHand: ComputedRef<Card[]>
  /** Selected card indices */
  selectedCards: Ref<Set<number>>
  /** Whether it's the player's turn */
  isMyTurn: ComputedRef<boolean>
  /** Current turn phase */
  turnPhase: ComputedRef<string>
  /** Callback when play is completed to a meld */
  onPlayToMeld?: (originalIndices: number[], meldIndex: number) => void
  /** Callback when discard action should occur */
  onDiscard?: () => void
}

// ============================================================================
// Constants
// ============================================================================

/** Min distance before deciding drag intent */
const INTENT_THRESHOLD_PX = 15

/** Degrees from horizontal for reorder intent (vs play) */
const HORIZONTAL_ANGLE_LIMIT = 30

// ============================================================================
// Composable
// ============================================================================

export function useCardDrag() {
  // External dependencies - stored as single options object via setup()
  let deps: UseCardDragOptions | null = null

  // ============================================================================
  // Drag State
  // ============================================================================

  /** Current drag intent (reset each drag) */
  const dragIntent = ref<DragIntent>('undecided')

  /** Starting position for intent calculation */
  const dragStartPos = ref<{ x: number; y: number } | null>(null)

  /** Whether user has manually reordered cards this round */
  const hasManuallyReordered = ref(false)

  /** Order of card indices when manual reorder is active */
  const manualCardOrder = ref<number[]>([])

  /** Source card index for drag */
  const dragSourceIndex = ref<number | null>(null)

  /** Target card index for reorder */
  const dragTargetIndex = ref<number | null>(null)

  /** Whether currently in sort drag mode */
  const isDraggingForSort = ref(false)

  /** Whether currently in play drag mode */
  const isDraggingToPlay = ref(false)

  /** General dragging flag */
  const isDragging = ref(false)

  /** Meld index being hovered during play drag */
  const dragOverMeldIndex = ref<number | null>(null)

  /** Whether hovering over discard pile */
  const dragOverDiscard = ref(false)

  /** Highlighted meld ID during drag */
  const highlightedMeldId = ref<string | null>(null)

  /** Flag to prevent click handler from re-toggling selection */
  const selectionHandledByPointer = ref(false)

  // ============================================================================
  // Computed Properties
  // ============================================================================

  /**
   * Cards to display in hand - uses manual order when player has reordered
   */
  const handCardsToDisplay = computed((): Card[] => {
    const hand = deps?.playerHand?.value || []
    // Use manual order if player has reordered this round and order is valid
    if (hasManuallyReordered.value && manualCardOrder.value.length === hand.length) {
      return manualCardOrder.value
        .map((i) => hand[i])
        .filter((c): c is Card => c !== undefined)
    }
    // Default: auto-sorted hand
    return hand
  })

  /**
   * Maps display index to original hand index (for server actions)
   * Uses originalHand (unsorted server order) for correct index mapping
   */
  const sortedToOriginalMap = computed((): number[] => {
    const originalHand = deps?.originalHand?.value || []
    return handCardsToDisplay.value.map((card) => {
      // Find matching card in original hand by comparing suit, rank, and deck_index
      return originalHand.findIndex(
        (c) => c.suit === card.suit && c.rank === card.rank && c.deck_index === card.deck_index
      )
    })
  })

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Detect drag intent based on movement direction
   * Returns 'reorder' for horizontal drags, 'play' for upward drags
   */
  function detectDragIntent(
    startX: number,
    startY: number,
    currentX: number,
    currentY: number
  ): DragIntent {
    const dx = currentX - startX
    const dy = startY - currentY // Positive = upward

    const distance = Math.sqrt(dx * dx + dy * dy)
    if (distance < INTENT_THRESHOLD_PX) {
      return 'undecided'
    }

    // Calculate angle from horizontal (0° = horizontal, 90° = vertical)
    const angleFromHorizontal = Math.abs(
      Math.atan2(Math.abs(dy), Math.abs(dx)) * (180 / Math.PI)
    )

    // Horizontal drag = reorder, upward drag = play
    return angleFromHorizontal <= HORIZONTAL_ANGLE_LIMIT ? 'reorder' : 'play'
  }

  /**
   * Move card from one position to another during reorder
   * Also updates selectedCards indices to maintain correct selection
   */
  function moveCard(fromIdx: number, toIdx: number): void {
    if (fromIdx === toIdx) return
    if (fromIdx < 0 || toIdx < 0) return
    if (
      fromIdx >= manualCardOrder.value.length ||
      toIdx >= manualCardOrder.value.length
    )
      return

    const order = [...manualCardOrder.value]
    const [moved] = order.splice(fromIdx, 1)
    if (moved !== undefined) {
      order.splice(toIdx, 0, moved)
      manualCardOrder.value = order

      // Update selectedCards indices to match new positions
      const selected = deps?.selectedCards?.value
      if (selected) {
        const newSelected = new Set<number>()
        selected.forEach((oldIdx: number) => {
          if (oldIdx === fromIdx) {
            // The moved card goes to its new position
            newSelected.add(toIdx)
          } else if (fromIdx < toIdx) {
            // Card moved right: indices between shift left
            if (oldIdx > fromIdx && oldIdx <= toIdx) {
              newSelected.add(oldIdx - 1)
            } else {
              newSelected.add(oldIdx)
            }
          } else {
            // Card moved left: indices between shift right
            if (oldIdx >= toIdx && oldIdx < fromIdx) {
              newSelected.add(oldIdx + 1)
            } else {
              newSelected.add(oldIdx)
            }
          }
        })
        if (deps?.selectedCards) {
          deps.selectedCards.value = newSelected
        }
      }
    }
  }

  /**
   * Reset to auto-sort mode
   */
  function resetToAutoSort(): void {
    hasManuallyReordered.value = false
    manualCardOrder.value = []
  }

  /**
   * Reset all drag state
   */
  function resetDragState(): void {
    dragStartPos.value = null
    dragSourceIndex.value = null
    dragTargetIndex.value = null
    dragIntent.value = 'undecided'
    isDraggingForSort.value = false
    isDraggingToPlay.value = false
    isDragging.value = false
    dragOverMeldIndex.value = null
    dragOverDiscard.value = false
    highlightedMeldId.value = null
    document.body.style.userSelect = ''

    // Remove dragging class from all cards
    document.querySelectorAll('.hand-card.dragging').forEach((el) => {
      el.classList.remove('dragging')
    })
  }

  // ============================================================================
  // Pointer Event Handlers
  // ============================================================================

  /**
   * Handle pointer down on card - starts tracking for intent detection
   */
  function handleCardPointerDown(
    event: PointerEvent,
    index: number,
    _card: Card
  ): void {
    // Store start position for intent detection
    dragStartPos.value = { x: event.clientX, y: event.clientY }
    dragSourceIndex.value = index
    dragIntent.value = 'undecided'

    // Capture pointer for tracking
    const target = event.currentTarget as HTMLElement
    target.setPointerCapture(event.pointerId)

    // Add card to selection if not already selected
    const selected = deps?.selectedCards?.value
    if (selected && !selected.has(index)) {
      const newSelected = new Set(selected)
      newSelected.add(index)
      if (deps?.selectedCards) {
        deps.selectedCards.value = newSelected
      }
      selectionHandledByPointer.value = true
    }
  }

  /**
   * Handle pointer move - detects intent and executes appropriate drag behavior
   */
  function handleCardPointerMove(event: PointerEvent): void {
    if (dragStartPos.value === null || dragSourceIndex.value === null) return

    const currentX = event.clientX
    const currentY = event.clientY

    // If intent not yet decided, try to detect it
    if (dragIntent.value === 'undecided') {
      const newIntent = detectDragIntent(
        dragStartPos.value.x,
        dragStartPos.value.y,
        currentX,
        currentY
      )

      // If still undecided, wait for more movement
      if (newIntent === 'undecided') return

      // Lock in the intent
      dragIntent.value = newIntent

      const hand = deps?.playerHand?.value || []

      // Set up appropriate mode based on intent
      if (newIntent === 'reorder') {
        // Initialize manual order if first time this round
        if (!hasManuallyReordered.value) {
          manualCardOrder.value = hand.map((_, i) => i)
          hasManuallyReordered.value = true
        }
        isDraggingForSort.value = true
        document.body.style.userSelect = 'none'
      } else {
        // Play intent - check if allowed
        const myTurn = deps?.isMyTurn?.value ?? false
        const phase = deps?.turnPhase?.value ?? ''
        if (!myTurn || phase === 'draw') {
          // Can't play now, reset
          resetDragState()
          return
        }
        isDraggingToPlay.value = true
        isDragging.value = true
        document.body.style.userSelect = 'none'

        // Add dragging class to selected cards
        const selected = deps?.selectedCards?.value
        if (selected) {
          const indices = Array.from(selected)
          indices.forEach((i) => {
            const cardEl = document.querySelector(`.hand-card[data-index="${i}"]`)
            cardEl?.classList.add('dragging')
          })
        }
      }
    }

    // Handle based on locked intent
    if (dragIntent.value === 'reorder') {
      handleReorderMove(event)
    } else if (dragIntent.value === 'play') {
      handlePlayMove(event)
    }
  }

  /**
   * Handle reorder movement - find insertion point based on horizontal position
   */
  function handleReorderMove(event: PointerEvent): void {
    const handContainer = document.querySelector('.player-hand')
    if (!handContainer) return

    const cards = handContainer.querySelectorAll('.hand-card')
    let targetIndex = dragSourceIndex.value!

    cards.forEach((card, idx) => {
      if (idx === dragSourceIndex.value) return

      const rect = card.getBoundingClientRect()
      const cardCenterX = rect.left + rect.width / 2

      if (event.clientX < cardCenterX && idx < dragSourceIndex.value!) {
        targetIndex = Math.min(targetIndex, idx)
      } else if (event.clientX > cardCenterX && idx > dragSourceIndex.value!) {
        targetIndex = Math.max(targetIndex, idx)
      }
    })

    dragTargetIndex.value = targetIndex
  }

  /**
   * Handle play movement - highlight valid meld targets under pointer
   */
  function handlePlayMove(event: PointerEvent): void {
    // Find elements under pointer
    const elementsUnderPointer = document.elementsFromPoint(
      event.clientX,
      event.clientY
    )

    // Check for meld targets
    const meldElement = elementsUnderPointer.find(
      (el) =>
        el.classList.contains('canasta-stack') ||
        el.classList.contains('meld-pile') ||
        el.closest('.canasta-stack')
    ) as HTMLElement | undefined

    if (meldElement) {
      const stack = meldElement.closest('.canasta-stack') as HTMLElement | null
      const meldIndex = stack?.dataset.meldIndex
      dragOverMeldIndex.value = meldIndex ? parseInt(meldIndex, 10) : null
    } else {
      dragOverMeldIndex.value = null
    }

    // Check for discard pile
    const discardElement = elementsUnderPointer.find(
      (el) =>
        el.classList.contains('pile-container') || el.closest('.pile-container')
    )
    dragOverDiscard.value = !!discardElement
  }

  /**
   * Handle pointer up - complete the drag action based on intent
   */
  function handleCardPointerUp(event: PointerEvent): void {
    // Release pointer capture
    const target = event.currentTarget as HTMLElement
    if (target && typeof target.releasePointerCapture === 'function') {
      try {
        target.releasePointerCapture(event.pointerId)
      } catch {
        // Ignore if pointer capture wasn't set
      }
    }

    if (dragIntent.value === 'reorder') {
      // Complete the reorder
      if (
        dragSourceIndex.value !== null &&
        dragTargetIndex.value !== null &&
        dragSourceIndex.value !== dragTargetIndex.value
      ) {
        moveCard(dragSourceIndex.value, dragTargetIndex.value)
      }
    } else if (dragIntent.value === 'play') {
      // Complete the play action
      if (dragOverMeldIndex.value !== null && deps?.onPlayToMeld) {
        const selected = deps?.selectedCards?.value
        if (selected) {
          const sortedIndices = Array.from(selected)
          const originalIndices = sortedIndices
            .map((i: number) => sortedToOriginalMap.value[i])
            .filter((x: number | undefined): x is number => x !== undefined)
          deps.onPlayToMeld(originalIndices, dragOverMeldIndex.value)
        }
      } else if (dragOverDiscard.value && deps?.onDiscard) {
        const phase = deps?.turnPhase?.value ?? ''
        if (phase === 'discard') {
          deps.onDiscard()
        }
      }
    }

    // Reset all drag state
    resetDragState()
  }

  /**
   * Handle pointer cancel (e.g., touch interrupted)
   */
  function handleCardPointerCancel(): void {
    resetDragState()
  }

  /**
   * Handle mouse enter - updates sort target during drag
   */
  function handleCardMouseEnter(index: number): void {
    // Update target during sort drag
    if (isDraggingForSort.value && dragSourceIndex.value !== null) {
      dragTargetIndex.value = index
    }
  }

  // ============================================================================
  // Setup Function
  // ============================================================================

  /**
   * Configure the composable with external dependencies
   */
  function setup(options: UseCardDragOptions): void {
    deps = options
  }

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // State
    dragIntent,
    dragStartPos,
    hasManuallyReordered,
    manualCardOrder,
    dragSourceIndex,
    dragTargetIndex,
    isDraggingForSort,
    isDraggingToPlay,
    isDragging,
    dragOverMeldIndex,
    dragOverDiscard,
    highlightedMeldId,
    selectionHandledByPointer,

    // Computed
    handCardsToDisplay,
    sortedToOriginalMap,

    // Methods
    detectDragIntent,
    moveCard,
    resetToAutoSort,
    resetDragState,
    handleCardPointerDown,
    handleCardPointerMove,
    handleCardPointerUp,
    handleCardPointerCancel,
    handleCardMouseEnter,

    // Setup
    setup
  }
}
